import { useEffect, useState } from "react";
import { Bot, Check, Copy, Eye, EyeOff, Globe, PlugZap, RefreshCw, ShieldAlert, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

const Bloc = ({ titre, contenu, testid }) => {
  const [copie, setCopie] = useState(false);
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900 overflow-hidden" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-white/10">
        <span className="text-[11px] uppercase tracking-[0.18em] text-white/50">{titre}</span>
        <button
          data-testid={`${testid}-copy`}
          onClick={async () => {
            await navigator.clipboard.writeText(contenu);
            setCopie(true);
            toast.success("Copié");
            setTimeout(() => setCopie(false), 2500);
          }}
          className="text-[11px] font-semibold text-[#4D93FF] inline-flex items-center gap-1.5 hover:text-white"
        >
          {copie ? <Check size={12} /> : <Copy size={12} />} Copier
        </button>
      </div>
      <pre className="px-3 py-3 text-[11px] leading-relaxed text-white/85 whitespace-pre-wrap break-all font-mono-bk">{contenu}</pre>
    </div>
  );
};

/** Réglage du serveur MCP : clé de service, configuration Claude Desktop / Claude Code, audit. */
export const ReglagesMCP = () => {
  const [conf, setConf] = useState(null);
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState(null);

  const load = (reveal = false) =>
    api.get("/admin/mcp/config", { params: { reveal } }).then(({ data }) => {
      setConf(data);
      setVisible(data.revelee);
    });

  useEffect(() => {
    load(false);
  }, []);

  const purger = async () => {
    if (!window.confirm("Vider le journal des appels de Claude ?")) return;
    try {
      const { data } = await api.delete("/admin/mcp/journal");
      toast.success(`${data.supprimes} appel(s) effacé(s)`);
      load(visible);
    } catch (e) {
      toast.error(errMsg(e, "Purge impossible"));
    }
  };

  const regenerer = async () => {
    if (!window.confirm("Régénérer le jeton du connecteur distant ? Claude devra être reconfiguré avec le nouveau jeton, l'ancien cessera immédiatement de fonctionner.")) return;
    setBusy(true);
    try {
      await api.post("/admin/mcp/remote-token");
      toast.success("Nouveau jeton généré");
      await load(true);
    } catch (e) {
      toast.error(errMsg(e, "Régénération impossible"));
    } finally {
      setBusy(false);
    }
  };

  const tester = async () => {
    setBusy(true);
    setTest(null);
    try {
      const { data } = await api.post("/admin/mcp/test");
      setTest(data);
      data.ok ? toast.success(data.detail) : toast.error(data.detail);
    } catch (e) {
      toast.error(errMsg(e, "Test impossible"));
    } finally {
      setBusy(false);
    }
  };

  if (!conf) return null;

  const cle = visible ? conf.cle : conf.cle || "non configurée";
  const json = `{
  "mcpServers": {
    "bkprod": {
      "command": "/Users/VOTRE_UTILISATEUR/bkprod-mcp/.venv/bin/python",
      "args": ["/Users/VOTRE_UTILISATEUR/bkprod-mcp/bkprod_mcp.py"],
      "env": {
        "BKPROD_API_BASE_URL": "${conf.base_url}",
        "BKPROD_API_KEY": "${cle}",
        "PYTHONUNBUFFERED": "1"
      }
    }
  }
}`;
  const cli = `claude mcp add --scope user --transport stdio bkprod \\
  --env BKPROD_API_BASE_URL=${conf.base_url} \\
  --env BKPROD_API_KEY=${cle} \\
  -- ~/bkprod-mcp/.venv/bin/python ~/bkprod-mcp/bkprod_mcp.py`;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4" data-testid="reglages-mcp">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="bk-label flex items-center gap-2">
          <Bot size={13} /> Serveur MCP — Claude pilote BKPROD
        </div>
        <span
          data-testid="mcp-etat"
          className={`text-xs px-2 py-1 rounded-full border ${
            conf.cle_configuree
              ? "text-emerald-700 bg-emerald-50 border-emerald-200"
              : "text-amber-700 bg-amber-50 border-amber-200"
          }`}
        >
          {conf.cle_configuree ? `Clé active · ${conf.appels} appel(s)` : "Clé non configurée"}
        </span>
      </div>

      <p className="text-xs text-slate-600">
        Cette clé donne à Claude un accès administrateur complet à cet environnement. Chaque appel est
        journalisé dans « Journal accès ». Ne la partagez pas et ne la mettez jamais dans un dépôt Git.
      </p>

      {!conf.cle_configuree ? (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50/60 px-3 py-2 text-xs text-amber-800" data-testid="mcp-sans-cle">
          <ShieldAlert size={14} className="mt-0.5 shrink-0" />
          Aucune clé sur cet environnement. Ajoutez la variable <span className="font-mono-bk">MCP_API_KEY</span>{" "}
          aux variables d'environnement, puis redéployez.
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono-bk text-xs bg-slate-100 rounded px-2 py-1.5 text-slate-800 break-all max-w-full" data-testid="mcp-cle">
              {conf.cle}
            </span>
            <Button data-testid="mcp-reveal-btn" variant="outline" size="sm" onClick={() => load(!visible)}>
              {visible ? <EyeOff size={13} className="mr-1" /> : <Eye size={13} className="mr-1" />}
              {visible ? "Masquer" : "Afficher la clé"}
            </Button>
            <Button data-testid="mcp-purge-btn" variant="outline" size="sm" onClick={purger} className="text-slate-600">
              <Trash2 size={13} className="mr-1" /> Vider le journal
            </Button>
          </div>

          {!visible && (
            <p className="text-xs text-slate-400" data-testid="mcp-hint">
              Affichez la clé pour obtenir les configurations complètes, prêtes à coller.
            </p>
          )}

          <Bloc titre="Claude Desktop — ~/Library/Application Support/Claude/claude_desktop_config.json" contenu={json} testid="mcp-bloc-desktop" />
          <Bloc titre="Claude Code — terminal" contenu={cli} testid="mcp-bloc-cli" />
          <Bloc
            titre="Installation sur le Mac (une seule fois)"
            contenu={`mkdir -p ~/bkprod-mcp && cd ~/bkprod-mcp
# copiez-y le fichier bkprod_mcp.py fourni avec l'application
python3 -m venv .venv && source .venv/bin/activate
pip install "mcp[cli]==2.1.1" httpx
# puis quittez complètement Claude (Cmd-Q) et relancez-le`}
            testid="mcp-bloc-install"
          />

          <div className="pt-4 border-t border-slate-200 space-y-3" data-testid="mcp-distant">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="bk-label flex items-center gap-2">
                <Globe size={13} /> Connecteur distant (Claude web et mobile)
              </div>
              <span
                data-testid="mcp-distant-etat"
                className={`text-xs px-2 py-1 rounded-full border ${
                  conf.distant_actif
                    ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                    : "text-amber-700 bg-amber-50 border-amber-200"
                }`}
              >
                {conf.distant_actif ? "Actif" : "Inactif"}
              </span>
            </div>
            <p className="text-xs text-slate-600">
              Dans Claude : Réglages → Connecteurs → Ajouter un connecteur personnalisé, puis collez l'URL
              ci-dessous et le jeton en en-tête <span className="font-mono-bk">Authorization: Bearer …</span>.
              Ce jeton est indépendant de la clé locale : le régénérer ne coupe pas Claude Desktop.
            </p>
            <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50/60 px-3 py-2 text-xs text-amber-800" data-testid="mcp-distant-avertissement">
              <ShieldAlert size={14} className="mt-0.5 shrink-0" />
              L'URL et le jeton affichés ici ne valent que pour <b>cet environnement</b>. Pour brancher Claude
              sur le site en ligne, copiez-les depuis le back-office de production (bkpro.tech) : un jeton de
              préproduction y sera refusé, et Claude affichera « connecté » tout en échouant à chaque appel.
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono-bk text-xs bg-slate-100 rounded px-2 py-1.5 text-slate-800 break-all max-w-full" data-testid="mcp-distant-jeton">
                {conf.distant_jeton || "aucun jeton"}
              </span>
              <Button data-testid="mcp-distant-test-btn" variant="outline" size="sm" onClick={tester} disabled={busy}>
                <PlugZap size={13} className="mr-1" /> {busy ? "Test…" : "Tester le connecteur"}
              </Button>
              <Button data-testid="mcp-distant-regen-btn" variant="outline" size="sm" onClick={regenerer} disabled={busy}>
                <RefreshCw size={13} className="mr-1" /> Révoquer et régénérer
              </Button>
            </div>
            {test && (
              <div
                data-testid="mcp-distant-test-resultat"
                className={`rounded-lg border px-3 py-2 text-xs ${
                  test.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-red-200 bg-red-50 text-red-800"
                }`}
              >
                {test.ok ? "Connecteur opérationnel : " : "Échec : "}
                {test.detail}
              </div>
            )}
            <Bloc titre="URL du connecteur (Streamable HTTP)" contenu={conf.distant_url} testid="mcp-bloc-url" />
            <Bloc
              titre="En-tête d'authentification à coller dans Claude"
              contenu={`Authorization: Bearer ${visible ? conf.distant_jeton : "AFFICHEZ_LA_CLE_POUR_VOIR_LE_JETON"}`}
              testid="mcp-bloc-bearer"
            />
            <Bloc
              titre="Vérification rapide (terminal)"
              contenu={`curl -s -X POST "${conf.distant_url}" \\
  -H "Authorization: Bearer ${visible ? conf.distant_jeton : "VOTRE_JETON"}" \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/json, text/event-stream" \\
  -d '{"jsonrpc":"2.0","id":1,"method":"tools/list"}'`}
              testid="mcp-bloc-curl"
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ReglagesMCP;
